import React from "react"
import { Spinner } from "../shared/Spinner"
import { RequestError } from "../shared/RequestError"
import BandPreviewBlock from "./BandPreviewBlock"
import { useEventPerformancesForPost } from "../../hooks/useEventPerformancesForPost"
import { EventPerformance } from "../../util/backend_api/event_performances"
import { nth, timeOnly, EventTimeFormat } from "@util/dates"
import { Link } from "react-router-dom"
import clsx from "clsx"
import type { DisplayMode } from "../../types/postComponentDisplay"

interface ConcertLineupProps {
  postId: number
  startTime?: string | null
  timezone?: string | null
  headerText?: string
  className?: string
  mode?: DisplayMode
}

const ConcertLineup: React.FC<ConcertLineupProps> = ({
  postId,
  startTime = null,
  timezone = null,
  headerText = "Lineup",
  className = "my-4",
  mode = "full",
}) => {
  const {
    isFetching,
    data: performances,
    error,
  } = useEventPerformancesForPost(postId, { enabled: mode !== "hide" })

  if (mode === "hide") return null

  if (isFetching) return <Spinner />
  if (error) return <RequestError error={error} />
  if (!performances || performances.length === 0) return null

  if (mode === "hint") {
    return (
      <p className={clsx("text-xs text-gray-500", className)}>
        {performances.length} {performances.length === 1 ? "band" : "bands"}{" "}
        performing
      </p>
    )
  }

  if (mode === "preview") {
    return (
      <p className={clsx("text-xs text-gray-600", className)}>
        {performances
          .map((performance: EventPerformance, i: number) => {
            const name = performance.band?.name || performance.pending_band_name
            return `${i + 1}. ${name}`
          })
          .join(" ")}
      </p>
    )
  }

  // full — original behavior
  return (
    <div className={className}>
      <ul>
        <h4 className="uppercase font-bold text-gray-700 text-xs">
          {headerText}
        </h4>

        {startTime && (
          <div className="mb-2 border-b border-gray-200 pb-2">
            <div className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-5 text-sm text-left">
                <div className="text-gray-400">Doors</div>
                <div className="text-gray-600 text-xs">
                  {EventTimeFormat(startTime, timezone)}
                </div>
              </div>
            </div>
          </div>
        )}

        {performances.map((performance: EventPerformance, index: number) => (
          <div
            className="mb-2 border-b border-gray-200 pb-2"
            data-performance-id={performance.id}
            key={performance.id}
          >
            <div className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-2 text-sm text-left overflow-hidden">
                <div className="text-gray-400">
                  {index + 1}
                  {nth(index + 1)}
                </div>
                <div className="text-gray-600 text-xs">
                  {timeOnly({ dateStr: performance.set_time })}
                </div>
              </div>

              <div className="lg:col-span-8 col-span-8 font-medium text-gray-900 leading-none">
                {performance.band ? (
                  <div className="flex items-center">
                    <Link
                      to={performance.band.band_path}
                      className={clsx(
                        "underline",
                        performance.band.is_verified
                          ? "font-black"
                          : "font-bold",
                      )}
                    >
                      {performance.band.name}
                    </Link>
                  </div>
                ) : (
                  performance.pending_band_name
                )}
              </div>

              <div className="lg:col-span-2 col-span-2 text-sm text-gray-400 flex justify-end">
                {performance.band ? (
                  <BandPreviewBlock
                    band={performance.band as any}
                    display="small"
                  />
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </ul>
      <div className="text-gray-500 text-xs italic mt-1">
        Set times have been known to change
      </div>
    </div>
  )
}

export default ConcertLineup
